import React from 'react';
import Loader from '~/components/atoms/loader/Loader';
import ProductFilters from '~/components/organisms/products/productFilters/productFilters';
import ProductLayout from '~/components/organisms/products/productLayout/ProductLayout';
import ProductResults from '~/components/organisms/products/productResults/productResults';
import { useQuerySet } from '~/contexts/QuerysetContext';
import { useConfig } from '~/core/configuration/configContext';
import { Filters_State } from '~/core/features/filtering/types/filters.type';
import { useSERPActions } from '~/core/state/actions/SERPActionsContext';
import { ProductResults_State } from '~/core/state/types/state-results.schema';
import { QuerySet_State } from '~/core/state/types/state-sets.type';
import { useCategoryResults } from '~/hooks/useCategoryResults';
import useInView from '~/hooks/useInView';
import { useDynamicComponents } from '~/plugins/dynamicComponents/dynamicComponentsContext';

type ProductContainerProps = {
    productsState: QuerySet_State;
    categorySetState?: QuerySet_State[];
    hasFulltextResults: boolean;
    productsTitleRef: React.RefObject<HTMLDivElement>;
};

const ProductsContainer = ({
    productsState,
    hasFulltextResults,
    categorySetState,
    productsTitleRef,
}: ProductContainerProps) => {
    const { Categories } = useDynamicComponents();
    const { result: productsResults, controls: productsControls } = useQuerySet<ProductResults_State>();

    const { loadMore, handleGoToResult, cancelFilter, cancelAllFilters, updateFilterControls, sortResults } =
        useSERPActions();
    const config = useConfig();

    const { categoryResults } = useCategoryResults();
    const categoryResultsCount = Math.max(...categoryResults.map((cat) => cat.result.hits.length)) || 0;
    const showCategories = categorySetState && categoryResults && categoryResultsCount > 0;
    const CategoriesComponent = showCategories ? (
        <Categories handleGoToResult={handleGoToResult} loadMore={loadMore} />
    ) : null;

    const showFilters = productsState.filters && productsState.filters.length > 0;
    // TODO: better without this additional looping
    const appliedFilters: Filters_State = productsControls.filters
        .filter((filter) => filter.applyFilter)
        .map((filterControls) => {
            const filter = productsState.filters.find((filter) => filter.identifier === filterControls.identifier);
            if (!filter) {
                throw new Error(`Filter ${filterControls.identifier} not found in productsState.filters`);
            }
            return filter;
        });

    const currency = config.layout.configuration.currency ?? 'CZK';

    const [productResultsRef, productResultsInView] = useInView({
        root: null,
        rootMargin: '0px',
        threshold: [0, 1],
    });

    if (!productsResults) {
        throw new Error('No results found for Products set: ${productsState.querySetIdentifier}');
    }

    if (
        productsResults.hits.length === 0 &&
        productsState.setting.showZeroResults === false &&
        appliedFilters.length === 0
    ) {
        return null;
    }

    return (
        <React.Suspense fallback={<Loader />}>
            <ProductLayout CategoriesComponent={CategoriesComponent} productsTitleRef={productsTitleRef}>
                {showFilters ? (
                    <ProductFilters
                        filtersState={productsState.filters}
                        filtersControls={productsControls.filters}
                        updateFilterControls={updateFilterControls}
                        currency={currency}
                        querySetIdentifier={productsState.querySet.identifier}
                        cancelAllFilters={cancelAllFilters}
                        productsResults={productsResults}
                        productResultsInView={productResultsInView}
                    />
                ) : null}

                <ProductResults
                    selectedSort={productsControls.sorting}
                    productResults={productsResults}
                    sortingMethods={productsState.sorting}
                    sortResults={sortResults}
                    loadMore={() =>
                        loadMore({
                            querySetIdentifier: productsState.querySet.identifier,
                            from: productsResults?.hits.length || 0,
                        })
                    }
                    customColorsConfiguration={{
                        labels: config.layout.configuration.labels,
                        deliveries: config.layout.configuration.deliveries,
                    }}
                    handleGoToResult={handleGoToResult}
                    appliedFiltersState={appliedFilters.filter(
                        (appliedFilter) => !appliedFilter.configuration.isSystem
                    )}
                    filtersControls={productsControls.filters.filter((filterControl) => !filterControl.isSystem)}
                    cancelFilter={cancelFilter}
                    cancelAllFilters={cancelAllFilters}
                    hasFulltextResults={hasFulltextResults}
                    categoryResultsCount={categoryResultsCount}
                    inViewRef={productResultsRef}
                />
            </ProductLayout>
        </React.Suspense>
    );
};

export default ProductsContainer;
